import { useMemo, useState } from 'react';
import Table from './ui/Table';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' });
};

function EntityTable({
  columns,
  rows,
  rowKey = 'id',
  onEdit,
  onDelete,
  onReorder,
  renderExtraActions,
  emptyMessage,
  searchPlaceholder,
  searchFilter,
  categoryFilter,
  yearRangeFilter
}) {
  const hasActions = Boolean(onEdit || onDelete || onReorder || renderExtraActions);
  const hasFilters = Boolean(searchFilter || categoryFilter || yearRangeFilter);
  const [query, setQuery] = useState('');
  const [categoryValue, setCategoryValue] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const availableYears = useMemo(() => {
    if (!yearRangeFilter) return [];
    const years = new Set();
    rows.forEach((row) => {
      const year = yearRangeFilter.getYear(row);
      if (year != null) years.add(year);
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [rows, yearRangeFilter]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filteredRows = useMemo(() => {
    let result = rows;
    if (searchFilter && query.trim()) {
      const needle = query.trim().toLowerCase();
      result = result.filter((row) => searchFilter(row, needle));
    }
    if (categoryFilter && categoryValue) {
      result = result.filter((row) => categoryFilter.getValue(row) === categoryValue);
    }
    if (yearRangeFilter && (yearFrom || yearTo)) {
      result = result.filter((row) => {
        const year = yearRangeFilter.getYear(row);
        if (year == null) return false;
        if (yearFrom && year < Number(yearFrom)) return false;
        if (yearTo && year > Number(yearTo)) return false;
        return true;
      });
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      const getValue = col?.sortValue || ((row) => row[sortKey]);
      result = [...result].sort((a, b) => compareValues(getValue(a), getValue(b)) * (sortDir === 'asc' ? 1 : -1));
    }
    return result;
  }, [rows, query, categoryValue, yearFrom, yearTo, searchFilter, categoryFilter, yearRangeFilter, sortKey, sortDir, columns]);

  // Un tri de colonne change l'ordre visuel sans rapport avec la position :
  // les flèches n'ont de sens que sur l'ordre naturel (filtré ou non).
  const canReorder = Boolean(onReorder) && !sortKey;

  const moveRow = (row, direction) => {
    const index = filteredRows.findIndex((r) => r[rowKey] === row[rowKey]);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || swapIndex < 0 || swapIndex >= filteredRows.length) return;
    const next = [...filteredRows];
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    onReorder(next.map((r) => r[rowKey]));
  };

  return (
    <>
      {hasFilters && (
        <div className="admin-table-filters">
          {searchFilter && (
            <Input
              placeholder={searchPlaceholder || 'Rechercher...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          {categoryFilter && (
            <Select
              value={categoryValue}
              onChange={(e) => setCategoryValue(e.target.value)}
              options={[{ value: '', label: categoryFilter.allLabel || 'Toutes les catégories' }, ...categoryFilter.options]}
            />
          )}
          {yearRangeFilter && (
            <>
              <Select
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                options={[{ value: '', label: 'De...' }, ...availableYears.map((y) => ({ value: y, label: y }))]}
              />
              <Select
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                options={[{ value: '', label: 'à...' }, ...availableYears.map((y) => ({ value: y, label: y }))]}
              />
            </>
          )}
        </div>
      )}
      <Table
        columns={columns}
        rows={filteredRows}
        rowKey={rowKey}
        emptyMessage={rows.length ? 'Aucun résultat pour ces filtres.' : emptyMessage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        renderActions={
          hasActions
            ? (row) => (
                <>
                  {onReorder && (
                    <>
                      <button
                        type="button"
                        className="admin-icon-btn"
                        onClick={() => moveRow(row, 'up')}
                        disabled={!canReorder || filteredRows[0]?.[rowKey] === row[rowKey]}
                        title="Monter"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="admin-icon-btn"
                        onClick={() => moveRow(row, 'down')}
                        disabled={!canReorder || filteredRows[filteredRows.length - 1]?.[rowKey] === row[rowKey]}
                        title="Descendre"
                      >
                        ↓
                      </button>
                    </>
                  )}
                  {renderExtraActions && renderExtraActions(row)}
                  {onEdit && (
                    <Button variant="secondary" onClick={() => onEdit(row)}>
                      Modifier
                    </Button>
                  )}
                  {onDelete && (
                    <Button variant="danger" onClick={() => onDelete(row)}>
                      Supprimer
                    </Button>
                  )}
                </>
              )
            : undefined
        }
      />
    </>
  );
}

export default EntityTable;
